import { useMemo, useState } from "react";
import { useTenants } from "../../../api/useTenants";
import { usePlanTemplatesMerged, type MergedPlanDisplay } from "../../../api/usePlanTemplatesMerged";
import type { TenantDto, TenantPlan } from "../../../api/types";

interface TenantStorage {
  id: string;
  name: string;
  tenantPlan: TenantPlan;
  usedMb: number;
  quotaMb: number;
  imagesMb: number;
  filesMb: number;
  videosMb: number;
  otherMb: number;
  lastUpdated: string;
  status: "active" | "suspended" | "trial";
}

function tenantToStorageRow(t: TenantDto, mergedByPlan: Record<TenantPlan, MergedPlanDisplay>): TenantStorage {
  const quotaMb = Math.max(1, mergedByPlan[t.plan].storage);
  const usedMb = t.storageUsed;
  // 优先使用后端真实统计；没有时回退到旧的估算拆分（避免历史环境接口不兼容）
  const hasBreakdown =
    typeof t.storageImages === "number" ||
    typeof t.storageFiles === "number" ||
    typeof t.storageVideos === "number" ||
    typeof t.storageOther === "number";
  const imagesMb = hasBreakdown ? Math.max(0, t.storageImages ?? 0) : Math.round(usedMb * 0.55);
  const filesMb = hasBreakdown ? Math.max(0, t.storageFiles ?? 0) : Math.round(usedMb * 0.25);
  const videosMb = hasBreakdown
    ? Math.max(0, t.storageVideos ?? 0)
    : Math.max(0, usedMb - imagesMb - filesMb - Math.round(usedMb * 0.05));
  const otherMb = hasBreakdown ? Math.max(0, t.storageOther ?? Math.max(0, usedMb - imagesMb - filesMb - videosMb)) : Math.max(0, usedMb - imagesMb - filesMb - videosMb);
  return {
    id: t.id,
    name: t.name,
    tenantPlan: t.plan,
    usedMb,
    quotaMb,
    imagesMb,
    filesMb,
    videosMb,
    otherMb,
    lastUpdated: t.lastActive,
    status: t.status === "suspended" ? "suspended" : t.plan === "trial" ? "trial" : "active",
  };
}

const statusConfig: Record<string, { label: string; color: string; dot: string }> = {
  active: { label: "运营中", color: "text-green-600", dot: "bg-green-500" },
  suspended: { label: "已停用", color: "text-red-500", dot: "bg-red-500" },
  trial: { label: "试用中", color: "text-amber-600", dot: "bg-amber-500" },
};

function formatMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

function UsageBar({ used, quota, warnAt = 80 }: { used: number; quota: number; warnAt?: number }) {
  const pct = Math.min(100, Math.round((used / quota) * 100));
  const color = pct >= 95 ? "bg-red-500" : pct >= warnAt ? "bg-amber-500" : "bg-teal-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-medium flex-shrink-0 ${pct >= 90 ? "text-red-600" : pct >= 80 ? "text-amber-600" : "text-gray-500"}`}>{pct}%</span>
    </div>
  );
}

export default function StorageAnalysis() {
  const { tenants } = useTenants();
  const { mergedByPlan } = usePlanTemplatesMerged();
  const storageRows = useMemo(
    () => tenants.map((t) => tenantToStorageRow(t, mergedByPlan)),
    [tenants, mergedByPlan],
  );
  const [sortBy, setSortBy] = useState<"usage" | "pct" | "name">("pct");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "suspended" | "trial">("all");
  const [selectedTenant, setSelectedTenant] = useState<TenantStorage | null>(null);

  const totalUsed = storageRows.reduce((s, t) => s + t.usedMb, 0);
  const totalQuota = storageRows.reduce((s, t) => s + t.quotaMb, 0);
  const overUsage = storageRows.filter((t) => t.quotaMb > 0 && t.usedMb / t.quotaMb >= 0.9);

  const filtered = storageRows.filter((t) => filterStatus === "all" || t.status === filterStatus);
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "usage") return b.usedMb - a.usedMb;
    if (sortBy === "pct") return (b.usedMb / b.quotaMb) - (a.usedMb / a.quotaMb);
    return a.name.localeCompare(b.name);
  });

  const typeColors = [
    { key: "imagesMb", label: "图片", color: "bg-red-400", textColor: "text-red-600", lightBg: "bg-red-50" },
    { key: "filesMb", label: "文件", color: "bg-amber-400", textColor: "text-amber-600", lightBg: "bg-amber-50" },
    { key: "videosMb", label: "视频", color: "bg-teal-400", textColor: "text-teal-600", lightBg: "bg-teal-50" },
    { key: "otherMb", label: "其他", color: "bg-gray-300", textColor: "text-gray-500", lightBg: "bg-gray-50" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-800">存储空间分析</h2>
          <p className="text-xs text-gray-400 mt-0.5">查看各租户存储使用情况，及时发现超量风险</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            上次打开本页：{new Date().toLocaleString("zh-CN", { dateStyle: "short", timeStyle: "short" })}
          </span>
          <button className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-white text-xs rounded-xl cursor-pointer hover:bg-amber-400 transition-colors whitespace-nowrap">
            <i className="ri-refresh-line"></i> 刷新数据
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "平台总存储", value: formatMb(totalUsed), sub: `共 ${formatMb(totalQuota)} 配额`,
            icon: "ri-database-2-line", color: "text-amber-600 bg-amber-50",
          },
          {
            label: "平均使用率", value: `${Math.round((totalUsed / totalQuota) * 100)}%`,
            sub: "全部租户平均", icon: "ri-pie-chart-2-line", color: "text-teal-600 bg-teal-50",
          },
          {
            label: "预警租户", value: `${overUsage.length} 家`,
            sub: "使用率超过 90%", icon: "ri-alert-line", color: "text-red-600 bg-red-50",
          },
          {
            label: "在线租户", value: `${storageRows.filter((t) => t.status === "active").length} 家`,
            sub: `共 ${storageRows.length} 家租户`, icon: "ri-building-4-line", color: "text-gray-600 bg-gray-100",
          },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${card.color}`}>
                <i className={`${card.icon} text-base`}></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{card.label}</p>
                <p className="text-lg font-bold text-gray-800 leading-tight">{card.value}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Warning Banner */}
      {overUsage.length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-5 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 text-base flex-shrink-0 mt-0.5"></i>
          <div>
            <p className="text-sm font-semibold text-red-700">存储预警</p>
            <p className="text-xs text-red-600 mt-0.5">
              以下租户存储使用率已超过 90%，建议扩容或联系租户清理数据：
              {overUsage.map((t) => (
                <span key={t.id} className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">{t.name}</span>
              ))}
            </p>
          </div>
        </div>
      )}

      {/* Filters + Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {(["all", "active", "suspended", "trial"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                  filterStatus === s ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                {{ all: "全部", active: "运营中", suspended: "已停用", trial: "试用中" }[s]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">排序：</span>
            {(["pct", "usage", "name"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-2.5 py-1 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                  sortBy === s ? "bg-amber-100 text-amber-700 font-medium" : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                {{ pct: "使用率", usage: "用量", name: "名称" }[s]}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">租户名称</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">套餐</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">已用 / 配额</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 min-w-[160px]">使用率</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">文件构成</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">状态</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((tenant, idx) => {
                const pct = Math.round((tenant.usedMb / tenant.quotaMb) * 100);
                const isWarning = pct >= 90;
                return (
                  <tr key={tenant.id} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${isWarning ? "bg-red-50/30" : ""}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-red-100 to-amber-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-red-600">{tenant.name.slice(0, 2)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{tenant.name}</p>
                          <p className="text-xs text-gray-400">更新于 {tenant.lastUpdated}</p>
                        </div>
                        {isWarning && <i className="ri-error-warning-fill text-red-500 text-sm flex-shrink-0"></i>}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs px-2 py-1 rounded-lg font-medium ${mergedByPlan[tenant.tenantPlan].color} ${mergedByPlan[tenant.tenantPlan].bgColor}`}>
                        {mergedByPlan[tenant.tenantPlan].label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-medium text-gray-800">{formatMb(tenant.usedMb)}</p>
                      <p className="text-xs text-gray-400">/ {formatMb(tenant.quotaMb)}</p>
                    </td>
                    <td className="px-4 py-3.5 min-w-[160px]">
                      <UsageBar used={tenant.usedMb} quota={tenant.quotaMb} />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1">
                        {typeColors.map((type) => {
                          const val = tenant[type.key as keyof TenantStorage] as number;
                          const typePct = Math.round((val / tenant.usedMb) * 100);
                          if (typePct < 1) return null;
                          return (
                            <div key={type.key} title={`${type.label}: ${formatMb(val)}`}
                              className={`flex items-center gap-1 text-[10px] ${type.textColor} ${type.lightBg} px-1.5 py-0.5 rounded-md cursor-default`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${type.color}`}></span>
                              {typePct}%
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[tenant.status].dot}`}></span>
                        <span className={`text-xs ${statusConfig[tenant.status].color}`}>{statusConfig[tenant.status].label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => setSelectedTenant(tenant)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        <i className="ri-bar-chart-2-line text-xs"></i> 详情
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant Detail Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl w-[560px] max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="font-bold text-gray-800">{selectedTenant.name} · 存储详情</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {mergedByPlan[selectedTenant.tenantPlan].label} · 上次更新 {selectedTenant.lastUpdated}
                </p>
              </div>
              <button onClick={() => setSelectedTenant(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Overall Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">总体使用情况</span>
                  <span className={`text-sm font-bold ${(selectedTenant.usedMb / selectedTenant.quotaMb) >= 0.9 ? "text-red-600" : "text-gray-700"}`}>
                    {formatMb(selectedTenant.usedMb)} / {formatMb(selectedTenant.quotaMb)}
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${(selectedTenant.usedMb / selectedTenant.quotaMb) >= 0.9 ? "bg-red-500" : "bg-teal-500"}`}
                    style={{ width: `${Math.min(100, (selectedTenant.usedMb / selectedTenant.quotaMb) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  剩余 {formatMb(selectedTenant.quotaMb - selectedTenant.usedMb)} · 使用率 {Math.round((selectedTenant.usedMb / selectedTenant.quotaMb) * 100)}%
                </p>
              </div>

              {/* Type Breakdown */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">按类型分布</p>
                <div className="space-y-3">
                  {typeColors.map((type) => {
                    const val = selectedTenant[type.key as keyof TenantStorage] as number;
                    const pct = Math.round((val / selectedTenant.usedMb) * 100);
                    return (
                      <div key={type.key}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${type.color}`}></span>
                            <span className="text-xs font-medium text-gray-600">{type.label}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">{formatMb(val)}</span>
                            <span className={`text-xs font-semibold ${type.textColor}`}>{pct}%</span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${type.color}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quota Upgrade Suggestion */}
              {(selectedTenant.usedMb / selectedTenant.quotaMb) >= 0.8 && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-lightbulb-line text-amber-500"></i>
                    <p className="text-sm font-semibold text-amber-700">建议扩容</p>
                  </div>
                  <p className="text-xs text-amber-600 leading-relaxed">
                    该租户存储使用率已达到 {Math.round((selectedTenant.usedMb / selectedTenant.quotaMb) * 100)}%，
                    建议联系租户升级套餐或主动清理过期文件，避免存储超限影响服务。
                  </p>
                  <button className="mt-3 flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-white text-xs rounded-lg cursor-pointer hover:bg-amber-600 transition-colors whitespace-nowrap">
                    <i className="ri-arrow-up-circle-line"></i> 建议升级套餐
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
